import * as Track from "@axon-enterprise/track";
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  TEMP_MX_PROPERTIES,
  mixpanelService,
  TEMP_MX_EVENTS,
} from "../common/tracking/MixpanelService";
import { QRRedirectLayout } from '../components';
import {
  ERROR,
  REACHABLE,
  SOURCE_QR_CODE,
  UNREACHABLE,
} from "../utils/constants";
import { checkFususDevice } from '../utils/deviceCheck';
import { clearLocalStorageOnDeviceMismatch } from '../utils/localStorage';
import { storeLocalStorageItem } from '../utils/sessionUtils';


const QRRedirectPage: React.FC = () => {
  const { sku, serial } = useParams<{ sku?: string; serial?: string }>();
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);
  const [deviceUnreachable, setDeviceUnreachable] = useState(false);

  const performDeviceCheck = useCallback(async (serialNumber: string) => {
    try {
      // Register durable context for all subsequent events
      mixpanelService.setMetadata({
        [TEMP_MX_PROPERTIES.ApplianceSerialNumber]: serial,
        [Track.MxPropertyName.Model]: sku,
        [Track.MxPropertyName.Source]: SOURCE_QR_CODE,
      });

      const deviceResult = await checkFususDevice(serialNumber);
      
      if (deviceResult.url) {
        // Device is reachable, set up redirect
        setRedirectUrl(deviceResult.url);
        mixpanelService.track(TEMP_MX_EVENTS.RedirectAttempted, {
          [Track.MxPropertyName.Status]: REACHABLE,
          [TEMP_MX_PROPERTIES.ConnectionMethod]: deviceResult.status,
          [Track.MxPropertyName.URL]: deviceResult.url,
        });
      } else {
        // Device is unreachable, show fallback page
        setDeviceUnreachable(true);
        mixpanelService.track(TEMP_MX_EVENTS.RedirectAttempted, {
          [Track.MxPropertyName.Status]: UNREACHABLE,
        });
      }
    } catch (error) {
      // On error, show fallback page
      setDeviceUnreachable(true);
      mixpanelService.track(TEMP_MX_EVENTS.RedirectAttempted, {
        [Track.MxPropertyName.Status]: ERROR,
        [Track.MxPropertyName.ErrorReason]: error instanceof Error ? error : new Error(String(error)),
        [TEMP_MX_PROPERTIES.ApplianceSerialNumber]: serialNumber,
        [Track.MxPropertyName.Model]: sku,
        [Track.MxPropertyName.Source]: SOURCE_QR_CODE,
      });
    }
  }, [serial, sku]);

  useEffect(() => {
    if (serial) {
      // Check if device parameters match stored values, clear if mismatch
      clearLocalStorageOnDeviceMismatch(serial, sku);

      // Store current serial and sku in localStorage
      storeLocalStorageItem('core_setup_device_serial_number', serial);
      if (sku) {
        storeLocalStorageItem('core_setup_device_sku', sku);
      }
      
      performDeviceCheck(serial);
    }
  }, [serial, sku, performDeviceCheck]);

  return (
    <QRRedirectLayout
      serial={serial}
      redirectUrl={redirectUrl}
      shouldRedirectToSession={deviceUnreachable}
    />
  );
};

export default QRRedirectPage;
